import { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NavLink, Outlet, useParams } from "react-router-dom";
import "../../css/Form.css";
import { ApiHelper } from "../../../utils/ApiHelper";
import { COMPANY_DETAIL, UPDATE_COMPANY, RAISE_CONCERN } from "../../../lib/API_KEYS";
import RejectionModal from "./RejectionModal";
import RaiseConcernModal from "./RaiseConcernModal";
import ActivityLogTable from "./ActivityLogTable";

const BuyerForm = ({ role }) => {
  const { id } = useParams();
  const [buyerData, setBuyerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [isAccepted, setIsAccepted] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [showConcernModal, setShowConcernModal] = useState(false);
  const [concern, setConcern] = useState("");
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    const fetchBuyerData = async () => {
      try {
        setLoading(true);
        const res = await new ApiHelper().FetchFromServer(
          COMPANY_DETAIL[0].replace(":companyId", id),
          COMPANY_DETAIL[1],
        );
        const result = res.data;
        setBuyerData(result || {});
        setIsAccepted(result?.status === "Approved");
        setIsFinalized(
          result?.status === "Approved" || result?.status === "Rejected"
        ); // ✅ Finalized if approved or rejected
      } catch (err) {
        setError("Failed to fetch data.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBuyerData();
  }, [id]);

  const handleUpdateStatus = async () => {
    if (isFinalized) return; // ✅ Prevent changing again
    setActionInProgress(true);
    setIsUpdating(true);
    const previousData = buyerData;
    setBuyerData((prev) => ({ ...prev, status: "Approved" }));
    setIsAccepted(true);

    try {
      const res = await new ApiHelper().FetchFromServer(
        UPDATE_COMPANY[0].replace(":companyId", id),
        UPDATE_COMPANY[1],
        undefined, { status: 'Approved' })

      const result = res.data;
      if (!result) {
        setBuyerData(previousData);
        setIsAccepted(false);
        throw new Error(result.message || "Failed to update status");
      }
      setIsFinalized(true); // ✅ Mark finalized once approved
    } catch (err) {
      setError("Error updating status: " + err.message);
      console.error("Error updating status:", err);
    } finally {
      setIsUpdating(false);
      setActionInProgress(false);
    }
  };

  const handleReject = async () => {
    if (isFinalized) return; // ✅ Prevent changing again
    if (!rejectionReason.trim()) return toast.error("Please provide a reason for rejection.");
    setActionInProgress(true);
    try {
      const res = await new ApiHelper().FetchFromServer(
        UPDATE_COMPANY[0].replace(":companyId", id),
        UPDATE_COMPANY[1],
        undefined, { status: 'Rejected', rejectionReason });
      if (!res) throw new Error("Failed to reject buyer");
      setShowRejectionModal(false);
      setBuyerData((prev) => ({ ...prev, status: "Rejected" }));
      setIsFinalized(true); // ✅ Mark finalized once rejected
    } catch (err) {
      setError("Error rejecting: " + err.message);
      console.error("Error rejecting:", err);
    } finally {
      setActionInProgress(false);
    }
  };

  // Concern Modal Handlers
  const handleOpenConcern = () => setShowConcernModal(true);
  const handleCloseConcern = () => {
    setShowConcernModal(false);
    setConcern("");
  };

  const handleSubmitConcern = async () => {
    if (!concern.trim()) return toast.error("Concern cannot be empty!");
    setActionInProgress(true);
    try {
      const res = await new ApiHelper().FetchFromServer(
        RAISE_CONCERN[0].replace(":companyId", id),
        RAISE_CONCERN[1],
        undefined,
        { concern }
      );

      if (!res.data) {
        throw new Error("Failed to submit concern");
      }

      setBuyerData((prev) => ({
        ...prev,
        status: "Query Raised",
        concern: concern,
        activityLog: res.data.activityLog || prev.activityLog
      }));

      toast.success("Concern submitted successfully! Status changed to 'Query Raised'.");
      handleCloseConcern();
    } catch (err) {
      toast.error("Failed to submit concern. Please try again.");
      console.error("Error submitting concern:", err);
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return (
      <div className="center-screen">
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="center-screen">
        <div className="error-text">{error}</div>
      </div>
    );
  }

  if (!buyerData) {
    return (
      <div className="center-screen">
        <div className="error-text">Buyer not found.</div>
      </div>
    );
  }

  const sections = ['Buyer', 'Project Manager'].includes(role) ? [
    { title: "Company Information", id: "company-info" },
  ] : [
    { title: "Company Information", id: "company-info" },
    { title: "Legal Information", id: "legal-info" },
    { title: "Financial Details", id: "financial-details" },
    { title: "Technical Capabilities", id: "tech-capabilities" },
    { title: "Certificates & Compliance", id: "cert-compliance" },
    { title: "References", id: "references" },
    { title: "Supplier Reference Program", id: "supplier-program" },
  ];

  return (
    <div className="app-container">
      <div className="top-border"></div>

      <div className="card">
        {/* Activity Log */}
        {buyerData.activityLog && buyerData.activityLog.length > 0 && (
          <div className="px-4 pt-4">
            <ActivityLogTable activityLog={buyerData.activityLog} />
          </div>
        )}

        {/* Header */}
        <div className="card-header">
          <div className="header-left">
            <div className="header-title">
              <h1>{buyerData.name || "Unknown Buyer"}</h1>
              <div
                className={`status-badge ${buyerData.status === "In Review"
                  ? "in-review"
                  : buyerData.status === "Query Raised"
                    ? "query-raised"
                    : buyerData.status === "Rejected"
                      ? "rejected status-pill"
                      : "accepted"
                  }`}
              >
                {buyerData.status || "N/A"}
              </div>
            </div>
            <p className="date">
              {buyerData.createdAt
                ? new Date(buyerData.createdAt).toLocaleString("en-US", {
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }) + " (7 days ago)"
                : "N/A"}
            </p>
          </div>

          <div className="header-actions">
            <button
              className="btn rejectBtn"
              onClick={() => setShowRejectionModal(true)}
              disabled={actionInProgress || isFinalized}
            >
              <img src="/images/reject2.svg" alt="Reject" className="icon" />
              Reject
            </button>

            <button
              className="btn blue"
              onClick={handleUpdateStatus}
              disabled={actionInProgress || isFinalized}
            >
              <img src="/images/accept.svg" alt="Accept" className="icon" />
              {isUpdating ? "Accept" : "Accept"}
            </button>

            <button
              className="btn gray"
              onClick={handleOpenConcern}
              disabled={actionInProgress || isFinalized}
            >
              <img
                src="/images/warning.svg"
                alt="Raise Concern"
                className="icon"
              />
              Raise a Concern
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="nav-tabs">
          {sections.map((section) => (
            <NavLink
              key={section.id}
              to={section.id}
              className={({ isActive }) =>
                `nav-tab ${isActive ? "active" : ""}`
              }
            >
              {section.title}
            </NavLink>
          ))}
        </div>

        {/* Content */}
        <div className="card-body">
          <Outlet context={buyerData} />
        </div>
      </div>

      <RaiseConcernModal
        show={showConcernModal}
        onClose={() => setShowConcernModal(false)}
        onSubmit={() => {
          handleSubmitConcern(concern);
        }}
        concern={concern}
        setConcern={setConcern}
      />

      <RejectionModal
        show={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onReject={() => {
          handleReject(rejectionReason);
        }}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
      />
      <ToastContainer />
    </div>
  );
};

export default BuyerForm;
